/**
 * Helpers for product license list UI and cart matching.
 */

export function formatLicensePrice(price) {
  if (price !== null && price !== undefined && price !== '' && Number(price) > 0) {
    return `${Number(price).toLocaleString('ru-RU')} ₽`;
  }
  return 'по запросу';
}

/** Display label for calculator additional service (supports priceLabel like «от 145 500 ₽»). */
export function formatAdditionalServicePrice(service) {
  if (service?.priceLabel) return String(service.priceLabel);
  const price = Number(service?.price);
  if (Number.isFinite(price) && price > 0) {
    return `${price.toLocaleString('ru-RU')} ₽${service?.isMonthly ? '/мес' : ''}`;
  }
  return 'по запросу';
}

/** Cart payload for an additional service: fixed price or «по запросу». */
export function buildAdditionalServiceCartItem(service) {
  const price = Number(service?.price);
  const hasPrice = Number.isFinite(price) && price > 0;
  return {
    name: service?.name || '',
    price: hasPrice ? price : null,
    priceDisplay: hasPrice
      ? `${price.toLocaleString('ru-RU')} ₽${service?.isMonthly ? '/мес' : ''}`
      : 'по запросу',
    isMonthly: Boolean(service?.isMonthly),
  };
}

export function getLicenseNumericPrice(license) {
  const raw = license?.price;
  if (raw === null || raw === undefined || raw === '') return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** True when license has a fixed numeric price (not «по запросу»). */
export function licenseHasNumericPrice(license) {
  return getLicenseNumericPrice(license) > 0;
}

/** Payload for admin email when user requests a quote for a license without price. */
export function buildPriceRequestCalculatorData(product, license, licenseIndex) {
  return {
    requestType: 'price_quote',
    productId: product?.id ?? null,
    productName: product?.name ?? null,
    licenseName: license?.name ?? null,
    licenseKey: getLicenseKey(license, licenseIndex),
    priceDisplay: 'по запросу',
  };
}

export function getLicenseKey(license, index) {
  if (license?.id !== null && license?.id !== undefined && String(license.id).trim() !== '') {
    return String(license.id);
  }
  return `idx:${index}`;
}

/**
 * Delivery terms for a single license: license-specific fields + vendor defaults.
 */
export function getLicenseDeliveryTerms(license, vendorDeliveryTerms = []) {
  const terms = [];

  if (license?.delivery?.trim()) {
    terms.push({ condition: 'Доставка', value: license.delivery.trim() });
  }
  if (license?.minimumOrder?.trim()) {
    terms.push({ condition: 'Период действия', value: license.minimumOrder.trim() });
  }

  if (Array.isArray(license?.deliveryTerms)) {
    license.deliveryTerms.forEach((term) => {
      if (term?.condition && term?.value) {
        terms.push({ condition: term.condition, value: term.value });
      }
    });
  }

  const vendorTerms = Array.isArray(vendorDeliveryTerms) ? vendorDeliveryTerms : [];
  vendorTerms.forEach((term) => {
    if (!term?.condition || !term?.value) return;
    if (!terms.some((t) => t.condition === term.condition)) {
      terms.push({ condition: term.condition, value: term.value });
    }
  });

  return terms;
}

export function buildLicenseCalculatorData(product, license, licenseIndex) {
  const basePrice = getLicenseNumericPrice(license);
  const hasPrice = basePrice > 0;

  return {
    userCount: 1,
    selectedServices: [],
    totalPrice: hasPrice ? basePrice : null,
    basePrice: hasPrice ? basePrice : null,
    monthlyPrice: hasPrice ? basePrice : null,
    oneTimePrice: null,
    priceDisplay: product?.price || 'по запросу',
    licenseIndex,
    licenseName: license?.name || null,
    licenseKey: getLicenseKey(license, licenseIndex),
  };
}

export function getCartItemProductId(item) {
  return (
    item?.product_id ??
    item?.productId ??
    item?.product?.id ??
    item?.product?.product_id ??
    null
  );
}

export function getCartItemCalculatorData(item) {
  return item?.calculator_data ?? item?.calculatorData ?? null;
}

/** Subtitle for cart/checkout rows: license name when present. */
export function getCartItemLicenseLabel(calc) {
  if (!calc || typeof calc !== 'object') return null;
  const name = calc.licenseName ?? calc.license_name;
  if (name === null || name === undefined) return null;
  const trimmed = String(name).trim();
  return trimmed || null;
}

export function getLicenseKeyFromCalculatorData(calc) {
  if (!calc) return null;
  if (calc.licenseKey) return String(calc.licenseKey);
  if (calc.licenseIndex !== null && calc.licenseIndex !== undefined) {
    return `idx:${calc.licenseIndex}`;
  }
  return null;
}

/**
 * @returns {{ keys: Set<string>, legacyProductInCart: boolean }}
 */
export function resolveLicenseKeysInCart(items, productId) {
  const keys = new Set();
  let legacyProductInCart = false;

  if (!Array.isArray(items) || !productId) {
    return { keys, legacyProductInCart };
  }

  items.forEach((item) => {
    const itemProductId = getCartItemProductId(item);
    if (String(itemProductId) !== String(productId)) return;

    const calc = getCartItemCalculatorData(item);
    const licenseKey = getLicenseKeyFromCalculatorData(calc);
    if (licenseKey) {
      keys.add(licenseKey);
    } else {
      legacyProductInCart = true;
    }
  });

  return { keys, legacyProductInCart };
}

export function isLicenseInCart({ keys, legacyProductInCart }, license, licenseIndex) {
  const licenseKey = getLicenseKey(license, licenseIndex);
  if (keys.has(licenseKey)) return true;
  if (legacyProductInCart && keys.size === 0) return true;
  return false;
}
